import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button, TextField } from "@mui/material";
import { IconX } from "@tabler/icons-react";

import {
  CommitListSearchOptions,
  LIST_SEARCH_MIN_CHARS,
} from "hooks/useSearchParamsOperations";

export type AssetsSearchFieldProps = {
  commitListSearch: (raw: string, opts?: CommitListSearchOptions) => void;
  commitListSearchSync: (raw: string, opts?: CommitListSearchOptions) => void;
  cancelCommitListSearch: () => void;
};

export const AssetsSearchField = memo(function AssetsSearchField({
  commitListSearch,
  commitListSearchSync,
  cancelCommitListSearch,
}: AssetsSearchFieldProps) {
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputLength, setInputLength] = useState(
    () => (searchParams.get("search") || "").length
  );

  const committedFromUrl = useMemo(
    () => searchParams.get("search") || "",
    [searchParams]
  );

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement !== el) {
      el.value = committedFromUrl;
      setInputLength(committedFromUrl.length);
    }
  }, [committedFromUrl]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputLength(value.length);
      commitListSearch(value);
    },
    [commitListSearch]
  );

  const handleClear = useCallback(() => {
    cancelCommitListSearch();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setInputLength(0);
    commitListSearchSync("");
  }, [
    cancelCommitListSearch,
    commitListSearchSync,
  ]);

  const helperText =
    inputLength > 0 && inputLength < LIST_SEARCH_MIN_CHARS
      ? `Enter at least ${LIST_SEARCH_MIN_CHARS} characters to search.`
      : undefined;

  return (
    <TextField
      inputRef={inputRef}
      defaultValue={committedFromUrl}
      onChange={handleChange}
      placeholder="Search assets..."
      variant="outlined"
      size="small"
      helperText={helperText}
      FormHelperTextProps={{ sx: { marginTop: 0.5 } }}
      sx={{
        width: { xs: "100%", sm: "300px" },
        backgroundColor: "#FFFFFF",
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "black",
          },
          "&:hover fieldset": {
            borderColor: "black",
          },
          "&.Mui-focused fieldset": {
            borderColor: "black",
          },
        },
      }}
      InputProps={{
        endAdornment:
          inputLength > 0 ? (
            <Button
              onClick={handleClear}
              sx={{
                minWidth: "auto",
                color: "black",
              }}
              aria-label="Clear search"
            >
              <IconX />
            </Button>
          ) : undefined,
      }}
    />
  );
});
